import { useState } from 'react'
import { useNotify, useRecordContext, useRefresh } from 'react-admin'
import {
  canAssignKits,
  canAssignBulkKits,
  canShipViaShopify,
  canCreateAllShippingLabels,
  canMarkAsFulfilled,
  canMarkAsManuallyFulfilled,
  formatMFHLineItemAndPatientDetail,
  getMFHPatientDetailFromLineItem,
  getOutboundShippingInfo,
  isShippingLabelPurchased,
  canManualReview,
  canError,
  canBeCancelled,
  canRevertToNew,
  canRevertToKitsAssignedReadyToShip,
  canRevertToShippingLabelPurchased,
} from './helpers/orderFieldsHelpers'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import {
  assignKits,
  createAllShippingLabels,
  markAsShipped,
  printAllShippingLabels,
  updateOrderStatus,
  cancelOrder,
} from '../../utils/progressOrderActions'

export const OrderCustomerDetailField = (props) => {
  const record = useRecordContext(props)
  return record ? (
    <div style={{ background: '#f0ad4e26', padding: '0.5em', paddingBottom: '-0.5em', solid: '#f0ad4ea8', position: 'relative' }}>
      {record.deIdentified && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: '#afafaf',
            color: 'white',
            padding: '0.25em 0.75em',
            borderRadius: '0 0 0 8px',
            fontWeight: 'bold',
            fontSize: '0.95em',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <i className="fa-solid fa-mask" title="De-Identified"></i>
        </div>
      )}
      <p>
        <strong>
          {record.shopifyData.customer?.first_name} {record.shopifyData.customer?.last_name}
        </strong>
      </p>
      <p>
        <strong>Email:</strong> {record.shopifyData.customer?.email ?? 'No Email Found'}
      </p>
      <p>
        <strong>Phone:</strong>{' '}
        {record.shopifyData.phone ||
          record.shopifyData.customer?.phone ||
          record.shopifyData.billing_address?.phone ||
          record.shopifyData.shipping_address?.phone}
      </p>
      <p>
        {record.shopifyData.shipping_address?.city || record.shopifyData.billing_address?.city || '-'},
        {record.shopifyData.shipping_address?.province_code || record.shopifyData.billing_address?.province_code || '-'}
      </p>
      <p>
        ${Number(record.shopifyData.total_price || record.shopifyData?.current_total_price_set?.shop_money?.amount || 0).toFixed(2)}{' '}
        USD{' '}
        <span style={{ color: (record.shopifyData.financial_status || 'unknown').toLowerCase() !== 'paid' ? 'red' : 'black' }}>
          ({record.shopifyData.financial_status || 'unknown'})
        </span>
      </p>
      <p>{new Date(record.shopifyData.created_at || 'unknown').toLocaleString()}</p>
    </div>
  ) : null
}

export const OrderItemsField = (props) => {
  const record = useRecordContext(props)
  if (!record || !record.shopifyData || !record.shopifyData.line_items) return null

  const line_items = record.shopifyData.line_items

  return line_items?.map((item, index) => {
    const MFHPatientDetail = getMFHPatientDetailFromLineItem(item)
    return <div key={index}>{formatMFHLineItemAndPatientDetail(item, MFHPatientDetail)}</div>
  })
}

export const OrderOutBoundShippingField = (props) => {
  const record = useRecordContext(props)

  if (!record) {
    return null
  }

  const output = getOutboundShippingInfo(record)

  return output.map((o, index) => (
    <Box key={index} display={'flex'} rowGap="16px" flexDirection="column" alignItems={'start'} width="max-content">
      {o}
    </Box>
  ))
}

export const ProgressOrderActions = (props) => {
  const record = useRecordContext(props)
  const notify = useNotify()
  const refresh = useRefresh()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [actionConfig, setActionConfig] = useState({ status: '', label: '', options: [] })
  const [noteOption, setNoteOption] = useState('')
  const [customNote, setCustomNote] = useState('')
  const [notifyCustomer, setNotifyCustomer] = useState(false)

  const handleOpenDialog = (status, label, options = []) => {
    setActionConfig({ status, label, options })
    setNoteOption(options.length > 0 ? options[0] : '')
    setCustomNote('')
    setDialogOpen(true)
    setNotifyCustomer(false) // Default to false
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
  }

  const handleConfirmAction = async () => {
    const finalNote = actionConfig.options.length > 0 && noteOption !== 'Other' ? noteOption : customNote

    // Close dialog first
    handleCloseDialog()

    if (actionConfig.status === 'canceled') {
      await cancelOrder([record], notify, refresh, finalNote, notifyCustomer)
    } else {
      await updateOrderStatus([record], notify, refresh, actionConfig.status, actionConfig.label, false, finalNote)
    }
  }

  const CANCEL_OPTIONS = [
    'Unable to complete order: kit was previously registered by your provider office on your behalf. Please contact your provider directly for test results.',
    'Unable to complete order: kit already registered',
    'Unable to complete order: registration code is invalid',
    'Unable to complete order: Kits purchased or registered for patients in the State of New York must provide a provider name to receive test results.',
    'Other'
  ]

  return (
    <Box display={'flex'} flexDirection="column" alignItems={'start'} width="max-content">
      {/* Kit Creation/Assignment */}

      {canAssignBulkKits(record) && (
        <Button
          type="button"
          onClick={async () => {
            await assignKits([record], notify)
            refresh()
          }}
        >
          <p>
            <span className="fa fa-boxes"></span> Assign Bulk Kits
          </p>
        </Button>
      )}

      {/* Kit Creation/Assignment */}

      {canAssignKits(record) && (
        <Button
          type="button"
          onClick={async () => {
            await assignKits([record], notify)
            refresh()
          }}
        >
          <p>
            <span className="fa fa-archive"></span> Assign Kits
          </p>
        </Button>
      )}

      {/* Do we need to send the user to Shopify Admin to ship this order? */}
      {canShipViaShopify(record) && (
        <Button
          href={`https://admin.shopify.com/store/malefromhome-com/orders/${record['orderId']}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-success"
        >
          <span className="fa fa-shipping-fast" title="Ship via Shopify"></span>
          Ship via Shopify &nbsp;
          <i className="fa-solid fa-up-right-from-square"></i>
        </Button>
      )}

      {/* Create Shipping Label */}

      {canCreateAllShippingLabels(record) && (
        <Button
          type="button"
          onClick={async () => {
            await createAllShippingLabels([record], notify)
            refresh()
          }}
        >
          <p>
            <span className="fa fa-barcode"></span> Create Shipping Labels
          </p>
        </Button>
      )}

      {/* Print Labels: Mark as Shipped */}

      {isShippingLabelPurchased(record) && (
        <>
          <Button
            type="button"
            onClick={async () => {
              await printAllShippingLabels([record], notify)
              refresh()
            }}
          >
            <p>
              <span className="fa fa-file-pdf-o"></span> Print All Labels
            </p>
          </Button>

          <Button
            type="button"
            onClick={async () => {
              await markAsShipped([record], notify, true, refresh)
              refresh()
            }}
          >
            <p>
              <span className="fa fa-envelope"></span> Mark as Shipped
            </p>
          </Button>
        </>
      )}
      {/* Mark as Fulfilled (no shipping)  */}

      {canMarkAsFulfilled(record) && (
        <Button
          type="button"
          onClick={async () => {
            await markAsShipped([record], notify, false, refresh)
            refresh()
          }}
        >
          <p>
            <span className="fa fa-check"></span> Mark as Fulfilled
          </p>
        </Button>
      )}

      {/* Mark as MANUALLY fulfilled (no shipping) */}
      {canMarkAsManuallyFulfilled(record) && (
        <Button
          type="button"
          onClick={async () => {
            await markAsShipped([record], notify, true, refresh)
            refresh()
          }}
        >
          <p>
            <span className="fa fa-check"></span> Mark as Manually Fulfilled
          </p>
        </Button>
      )}

      {/* Manual Review */}
      {canManualReview(record) && (
        <Button
          type="button"
          onClick={async () => {
            await updateOrderStatus([record], notify, refresh, 'manualReview', 'Manual Review')
          }}
        >
          <p>
            <span className="fa fa-eye"></span> Manual Review
          </p>
        </Button>
      )}

      {/* Error */}
      {canError(record) && (
        <Button
          type="button"
          onClick={async () => {
            await updateOrderStatus([record], notify, refresh, 'error', 'Error')
          }}
        >
          <p>
            <span className="fa fa-exclamation-triangle"></span> Error
          </p>
        </Button>
      )}

      {/* Cancel */}
      {canBeCancelled(record) && (
        <Button
          type="button"
          onClick={() => handleOpenDialog('canceled', 'Canceled', CANCEL_OPTIONS)}
        >
          <p>
            <span className="fa fa-times"></span> Cancel
          </p>
        </Button>
      )}

      {canRevertToNew(record) && (
        <Button
          type="button"
          onClick={async () => {
            await updateOrderStatus([record], notify, refresh, 'new', 'New', false)
          }}
        >
          <p>
            <span className="fa fa-undo"></span> Revert to New
          </p>
        </Button>
      )}

      {canRevertToKitsAssignedReadyToShip(record) && (
        <Button
          type="button"
          onClick={async () => {
            await updateOrderStatus([record], notify, refresh, 'kitsAssignedReadyToShip', 'Kits Assigned Ready to Ship', false)
          }}
        >
          <p>
            <span className="fa fa-undo"></span> Revert to Kits Assigned Ready to Ship
          </p>
        </Button>
      )}

      {canRevertToShippingLabelPurchased(record) && (
        <Button
          type="button"
          onClick={async () => {
            await updateOrderStatus([record], notify, refresh, 'shippingLabelPurchased', 'Shipping Label Purchased', false)
          }}
        >
          <p>
            <span className="fa fa-undo"></span> Revert to Shipping Label Purchased
          </p>
        </Button>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Note for {actionConfig.label}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            {actionConfig.options.length > 0 && (
              <FormControl fullWidth margin="dense">
                <InputLabel id="reason-select-label">Reason</InputLabel>
                <Select
                  labelId="reason-select-label"
                  value={noteOption}
                  label="Reason"
                  onChange={(e) => setNoteOption(e.target.value)}
                >
                  {actionConfig.options.map((opt) => (
                    <MenuItem key={opt} value={opt} style={{ whiteSpace: 'normal' }}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {(!actionConfig.options.length || noteOption === 'Other') && (
              <TextField
                autoFocus={!actionConfig.options.length}
                margin="dense"
                label="Note"
                fullWidth
                multiline
                rows={3}
                value={customNote}
                onChange={(e) => setCustomNote(e.target.value)}
                placeholder="Enter details..."
              />
            )}
            {actionConfig.status === 'canceled' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={notifyCustomer}
                    onChange={(e) => setNotifyCustomer(e.target.checked)}
                    color="primary"
                  />
                }
                label="Notify Customer"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirmAction} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export const OrderToolTip = () => {
  const record = useRecordContext()

  if (!record || !record.notes) {
    return <span />
  }

  return (
    <span style={{ color: '#3296F3', padding: '2px' }} className="fa-solid fa-file tooltip">
      <span className="tooltiptext">{record.notes && record.notes.replace(/"/g, '&quot;')}</span>
    </span>
  )
}
